/**
 * The month window the monthly charts draw.
 *
 * The x-axis labels each point with its month NAME only — the year is dropped —
 * so a multi-year range rendered "jan feb ... dec jan feb ... dec" and a reader
 * could not tell which year any point belonged to. Rather than widening every
 * label, the chart draws one year's worth and discloses that it has done so.
 *
 * Twelve is the window because the series carries a previous-year comparison:
 * more than twelve current-year points and that comparison has nothing to pair
 * each point against.
 */
export const MONTH_WINDOW = 12;

/** Anything carrying a "YYYY-MM" month, which is every monthly chart's item. */
interface HasMonth {
  month: string;
}

/**
 * The most recent `MONTH_WINDOW` months, oldest first.
 *
 * Sorted before slicing: "most recent twelve" is meaningless on an unordered
 * array and nothing in the response contract promises an order, so taking the
 * raw tail would silently pick an arbitrary twelve. Lexicographic sort is
 * correct here because "YYYY-MM" is zero-padded and fixed-width.
 *
 * `truncated` tells the caller whether anything was dropped, so the caption can
 * stay silent when the whole selection already fits.
 */
export function lastTwelveMonths<T extends HasMonth>(
  data: T[] | undefined | null
): { months: T[]; truncated: boolean } {
  if (!data || data.length === 0) return { months: [], truncated: false };
  const ordered = [...data].sort((a, b) => a.month.localeCompare(b.month));
  return {
    months: ordered.slice(-MONTH_WINDOW),
    truncated: ordered.length > MONTH_WINDOW,
  };
}
